import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import useCompanyHeader from '../../../../common/category/useCompanyHeader'
import { DownloadJpg } from '../../../../common/downloadJpg'
import { UseI18n } from '../../../../common/hooks/useI18n'
import { Element } from '../../../../common/html/element'
import { Panel } from '../../../../common/panel'
import {
  selectComponentAuthorized,
  selectUserType,
} from '../../../../layout/store/slice'
import { AUTHORIZATION_TYPE } from '../../../../utils/authorization'
import { selectCompanyInfo } from '../../../common/header/companyInfo/store/slice'
import { selectCompareIndustry, selectDefaultIndustry } from '../store/slice'
import ActivitiesRatiosAndRisk from './components/activitiesRatiosAndRisk'
import Commentary from './components/commentary'
import DistributionOfScore from './components/distributionOfScore'
import FinancialRatios from './components/financialRatios'
import PerformanceMatrix from './components/performanceMatrix'
import {
  exportGeneralAnalysis,
  getActivitiesRatioAndRisk,
  getActivitiesRatioAndRiskCompare,
  getCommentary,
  getDistributeScoreChart,
  getDistributeScoreCompareChart,
  getGenericRatioHighlight,
  getIndustryRatioDetail,
  getPerformanMatrixChartData,
  getPerformanMatrixDisplayData,
} from './store/thunk'

const GeneralAnalysis = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const label = UseI18n('companyAnalysis.industryAnalysis.GENERAL_ANALYSIS')
  const locale = useSelector((state) => state.i18n.locale)
  const vsicDefault = useSelector(selectDefaultIndustry)
  const vsicCompare = useSelector(selectCompareIndustry)
  const params = new Proxy(new URLSearchParams(location.search), {
    get: (searchParam, prop) => searchParam.get(prop),
  })
  const { organizationShortName, organizationName, taxCode } =
    useSelector(selectCompanyInfo)
  const userType = useSelector(selectUserType)
  const AUTHORIZATION_API = useSelector(selectComponentAuthorized)
  const { companyHeader } = useCompanyHeader({ disableCallApi: true })

  useEffect(() => {
    dispatch(
      getGenericRatioHighlight({
        VsicId: vsicDefault,
        CompareVsicId: vsicCompare,
      }),
    )
  }, [vsicDefault, vsicCompare])

  useEffect(() => {
    dispatch(
      getIndustryRatioDetail({
        MainVsicId: vsicDefault,
        CompareVsicId: vsicCompare,
      }),
    )
    dispatch(
      getActivitiesRatioAndRisk({
        MainVsicId: vsicDefault,
      }),
    )
    dispatch(
      getDistributeScoreChart({
        MainVsicId: vsicDefault,
      }),
    )
    if (vsicCompare) {
      dispatch(
        getDistributeScoreCompareChart({
          MainVsicId: vsicDefault,
          CompareVsicId: vsicCompare,
        }),
      )
      dispatch(
        getActivitiesRatioAndRiskCompare({
          MainVsicId: vsicDefault,
          CompareVsicId: vsicCompare,
        }),
      )
    }
    dispatch(
      getCommentary({
        MainVsicId: vsicDefault,
        CompareVsicId: vsicCompare,
      }),
    )
    dispatch(
      getPerformanMatrixChartData({
        MainVsicId: vsicDefault,
        CompareVsicId: vsicCompare,
      }),
    )
    dispatch(
      getPerformanMatrixDisplayData({
        MainVsicId: vsicDefault,
        CompareVsicId: vsicCompare,
      }),
    )
  }, [vsicDefault, vsicCompare, locale])

  const renderRightHeader = () => {
    const labelGeneralAnalysis = UseI18n(
      'companyAnalysis.industryAnalysis.GENERAL_ANALYSIS',
    )
    const labelPerformance = UseI18n(
      'companyAnalysis.industryAnalysis.PERFORMANCE_MATRIX',
    )
    const labelDistribution = UseI18n(
      'companyAnalysis.industryAnalysis.DISTRIBUTION_OF_SCORE',
    )

    const downloadCsv = () => {
      const permission =
        AUTHORIZATION_API.companyAnalysis.downloadPanel[userType.userTypeId]
      if (permission !== AUTHORIZATION_TYPE.ALLOW) return
      if (!companyHeader.allowDynamicReport) return
      dispatch(
        exportGeneralAnalysis({
          OrganizationId: params.organizationId,
          MainVsicId: vsicDefault,
          CompareVsicId: vsicCompare,
        }),
      )
    }

    return (
      <Element className="dFlex alignItemsCenter" onClick={downloadCsv}>
        <DownloadJpg
          data={{
            arrName: [
              labelGeneralAnalysis,
              labelPerformance,
              organizationShortName || organizationName || taxCode || '',
            ],
            titleJpg: labelPerformance,
            domId: 'performanceMatrixChartId',
          }}
        >
          <DownloadJpg
            data={{
              arrName: [
                labelGeneralAnalysis,
                labelDistribution,
                organizationShortName || organizationName || taxCode || '',
              ],
              titleJpg: labelDistribution,
              domId: 'distributionOfScoreChartId',
            }}
          />
        </DownloadJpg>
      </Element>
    )
  }

  return (
    <Panel label={label} renderRightHeader={renderRightHeader}>
      <div
        style={{
          padding: '8px 12px',
        }}
      >
        <div className="dFlex gap4">
          <div className="w50">
            <FinancialRatios />
            <ActivitiesRatiosAndRisk />
          </div>
          <div className="w50">
            <PerformanceMatrix />
            <DistributionOfScore />
          </div>
        </div>
        <Commentary />
      </div>
    </Panel>
  )
}

export default GeneralAnalysis
